import { useEffect, useMemo, useState, useCallback } from "react";
import { api, brl, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  RefreshCw, Search, ClipboardCheck, CheckCircle2, RotateCcw, Eye,
  AlertTriangle, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { key: "pendente", label: "Pendente" },
  { key: "parcial", label: "Parcial" },
  { key: "quitado", label: "Quitado" },
];
const STATUS_LABEL = Object.fromEntries(STATUSES.map((s) => [s.key, s.label]));

const ORIGENS = [
  { key: "manual", label: "Manual" },
  { key: "sinal_confirmado", label: "Sinal confirmado" },
  { key: "legado", label: "Legado" },
];

const FORMAS = [
  { key: "pix", label: "PIX" },
  { key: "cartao", label: "Cartão" },
  { key: "boleto", label: "Boleto" },
  { key: "dinheiro", label: "Dinheiro" },
  { key: "transferencia", label: "Transferência" },
];

function today() { return new Date().toISOString().slice(0, 10); }
function firstDayOfMonth() {
  const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function lastDayOfMonth() {
  const d = new Date(); const l = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${l.getFullYear()}-${String(l.getMonth() + 1).padStart(2, "0")}-${String(l.getDate()).padStart(2, "0")}`;
}

function StatusBadge({ status }) {
  const cls = {
    pendente: "bg-[#F5E4DD] text-[#7c3a20] border border-[#D4A652]/25",
    parcial: "bg-[#FFF4D6] text-[#8B6A1F] border border-[#8B6A1F]/25",
    quitado: "bg-[#EAF0EA] text-[#083A6B] border border-[#083A6B]/20",
  }[status] || "bg-stone-100 text-stone-700 border border-stone-300";
  return (
    <span data-testid={`conferencia-status-${status}`}
      className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${cls}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

export default function ConferenciaReservas() {
  const [me, setMe] = useState(null);
  const [pousadas, setPousadas] = useState([]);

  const [filtros, setFiltros] = useState({
    data_inicio: firstDayOfMonth(),
    data_fim: lastDayOfMonth(),
    pousada_id: "all",
    status: "all",
    origem: "all",
    somente_com_saldo: true,
  });
  const [busca, setBusca] = useState("");

  const [fila, setFila] = useState({ data: null, loading: true, error: null });

  const [detalheAlvo, setDetalheAlvo] = useState(null);
  const [detalheOpen, setDetalheOpen] = useState(false);
  const [pagamentos, setPagamentos] = useState([]);
  const [auditoria, setAuditoria] = useState([]);
  const [detalheLoading, setDetalheLoading] = useState(false);

  const [conferirAlvo, setConferirAlvo] = useState(null);
  const [conferirForm, setConferirForm] = useState({
    valor: "", data: today(), forma: "pix", observacao: "", referencia_externa: "", conta_financeira_id: "",
  });
  const [conferindo, setConferindo] = useState(false);
  const [contasFinanceiras, setContasFinanceiras] = useState([]);

  const [estornoAlvo, setEstornoAlvo] = useState(null);
  const [estornoMotivo, setEstornoMotivo] = useState("");
  const [estornando, setEstornando] = useState(false);

  const podeConferir = !!(me && (me.is_admin || me.is_finance));
  const podeEstornar = !!(me && me.is_admin); // /pagamentos/{id}/estornar é admin-only

  useEffect(() => {
    (async () => {
      try {
        const [u, po, cf] = await Promise.all([
          api.get("/auth/me").catch(() => ({ data: null })),
          api.get("/pousadas-list").catch(() => ({ data: [] })),
          api.get("/contas-financeiras", { params: { ativo: true } }).catch(() => ({ data: [] })),
        ]);
        setMe(u.data);
        setPousadas(Array.isArray(po.data) ? po.data.filter((x) => x.id) : []);
        const cfList = Array.isArray(cf.data) ? cf.data : [];
        setContasFinanceiras(cfList.filter((x) => x?.ativo !== false).sort((a, b) => (a.nome || "").localeCompare(b.nome || "")));
      } catch { /* silencioso */ }
    })();
  }, []);

  const pousadaMap = useMemo(() => Object.fromEntries(pousadas.map((p) => [p.id, p.nome])), [pousadas]);

  const loadFila = useCallback(async () => {
    setFila((s) => ({ ...s, loading: true, error: null }));
    try {
      const params = { somente_com_saldo: filtros.somente_com_saldo };
      if (filtros.data_inicio) params.data_inicio = filtros.data_inicio;
      if (filtros.data_fim) params.data_fim = filtros.data_fim;
      if (filtros.pousada_id !== "all") params.pousada_id = filtros.pousada_id;
      if (filtros.status !== "all") params.status = filtros.status;
      if (filtros.origem !== "all") params.origem = filtros.origem;
      const { data } = await api.get("/conferencia/fila", { params });
      setFila({ data, loading: false, error: null });
    } catch (err) {
      setFila({ data: null, loading: false, error: err?.response?.status === 401 ? "auth" : "erro" });
    }
  }, [filtros]);

  useEffect(() => { loadFila(); }, [loadFila]);

  const itensFiltrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    const arr = fila.data?.itens || [];
    if (!q) return arr;
    return arr.filter((i) => {
      const alvo = [i.hospede_nome, i.hospede_documento, i.pousada_nome, i.reserva_id]
        .filter(Boolean).join(" ").toLowerCase();
      return alvo.includes(q);
    });
  }, [fila.data, busca]);

  const abrirDetalhe = async (item) => {
    setDetalheAlvo(item); setDetalheOpen(true); setDetalheLoading(true);
    setPagamentos([]); setAuditoria([]);
    try {
      const [p, a] = await Promise.all([
        api.get(`/reservas/${item.reserva_id}/pagamentos`).catch(() => ({ data: [] })),
        api.get(`/reservas/${item.reserva_id}/pagamentos-auditoria`).catch(() => ({ data: [] })),
      ]);
      setPagamentos(Array.isArray(p.data) ? p.data : []);
      setAuditoria(Array.isArray(a.data) ? a.data : []);
    } catch { /* mantém básico */ }
    finally { setDetalheLoading(false); }
  };

  const abrirConferir = (item) => {
    if (!podeConferir) return toast.error("Sem permissão para conferir");
    setConferirAlvo(item);
    setConferirForm({
      valor: String(item.saldo || ""),
      data: today(),
      forma: "pix",
      observacao: "",
      referencia_externa: "",
      conta_financeira_id: "",
    });
  };

  const executarConferir = async () => {
    if (!conferirAlvo) return;
    const v = Number(conferirForm.valor);
    if (!v || v <= 0) return toast.error("Valor deve ser maior que zero");
    if (!conferirForm.data) return toast.error("Informe a data");
    if (!conferirForm.forma) return toast.error("Selecione a forma de pagamento");
    if (!conferirForm.conta_financeira_id) return toast.error("Selecione a Conta Financeira do recebimento");
    setConferindo(true);
    try {
      await api.post("/conferencia/pagamentos", {
        reserva_id: conferirAlvo.reserva_id,
        valor: v,
        data: conferirForm.data,
        forma: conferirForm.forma,
        observacao: conferirForm.observacao || "",
        referencia_externa: conferirForm.referencia_externa || null,
        conta_financeira_id: conferirForm.conta_financeira_id,
      });
      toast.success("Pagamento conferido");
      setConferirAlvo(null);
      loadFila();
      // Se o dialog de detalhe estiver aberto para a mesma reserva, recarrega
      if (detalheOpen && detalheAlvo?.reserva_id === conferirAlvo.reserva_id) {
        abrirDetalhe(conferirAlvo);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao registrar pagamento";
      toast.error(String(msg));
    } finally {
      setConferindo(false);
    }
  };

  const abrirEstorno = (pag) => {
    if (!podeEstornar) return toast.error("Estorno restrito a administradores");
    if (pag.estornado) return toast.error("Pagamento já estornado");
    setEstornoAlvo(pag); setEstornoMotivo("");
  };

  const executarEstorno = async () => {
    if (!estornoAlvo) return;
    if (!estornoMotivo.trim()) return toast.error("Motivo obrigatório");
    setEstornando(true);
    try {
      await api.post(`/pagamentos/${estornoAlvo.id}/estornar`, { motivo: estornoMotivo.trim() });
      toast.success("Pagamento estornado");
      setEstornoAlvo(null); setEstornoMotivo("");
      loadFila();
      if (detalheAlvo) abrirDetalhe(detalheAlvo);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erro ao estornar pagamento";
      toast.error(String(msg));
    } finally {
      setEstornando(false);
    }
  };

  return (
    <div data-testid="page-conferencia-reservas">
      <PageHeader
        eyebrow="Financeiro · Conferência"
        title="Conferência de Reservas"
        description="Fila oficial de reservas com saldo em aberto. Registrar pagamentos manuais (Admin/Financeiro) ou estornar (Admin). PagBank e /sinal/pagar permanecem intactos."
        actions={
          <Button variant="outline" size="sm" onClick={loadFila} data-testid="btn-refresh-conferencia">
            <RefreshCw size={14} /> Atualizar
          </Button>
        }
      />

      {fila.error === "auth" && (
        <div className="bg-[#F5E4DD] text-[#7c3a20] border border-[#D4A652]/25 rounded-lg px-4 py-2 text-sm mb-4">
          Sessão expirada. Faça login novamente.
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Entrada de</Label>
          <Input type="date" data-testid="filter-conferencia-de" value={filtros.data_inicio}
            onChange={(e) => setFiltros({ ...filtros, data_inicio: e.target.value })} className="w-[150px]" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Até</Label>
          <Input type="date" data-testid="filter-conferencia-ate" value={filtros.data_fim}
            onChange={(e) => setFiltros({ ...filtros, data_fim: e.target.value })} className="w-[150px]" />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Pousada</Label>
          <Select value={filtros.pousada_id} onValueChange={(v) => setFiltros({ ...filtros, pousada_id: v })}>
            <SelectTrigger data-testid="filter-conferencia-pousada" className="w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {pousadas.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Status</Label>
          <Select value={filtros.status} onValueChange={(v) => setFiltros({ ...filtros, status: v })}>
            <SelectTrigger data-testid="filter-conferencia-status" className="w-[150px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Origem</Label>
          <Select value={filtros.origem} onValueChange={(v) => setFiltros({ ...filtros, origem: v })}>
            <SelectTrigger data-testid="filter-conferencia-origem" className="w-[170px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {ORIGENS.map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Escopo</Label>
          <Select value={filtros.somente_com_saldo ? "saldo" : "todos"}
            onValueChange={(v) => setFiltros({ ...filtros, somente_com_saldo: v === "saldo" })}>
            <SelectTrigger data-testid="filter-conferencia-escopo" className="w-[170px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="saldo">Com saldo em aberto</SelectItem>
              <SelectItem value="todos">Todas as reservas</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[11px] uppercase tracking-widest text-stone-500">Busca</Label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input value={busca} data-testid="filter-conferencia-busca"
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Hóspede, documento, pousada, reserva..." className="pl-8" />
          </div>
        </div>
      </div>

      {/* KPIs */}
      {fila.data && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
          <div data-testid="kpi-conferencia-total" className="bg-[#FFFFFF] border border-stone-200 rounded-lg px-4 py-3">
            <div className="text-[11px] uppercase tracking-widest text-stone-500">Reservas na fila</div>
            <div className="font-serif text-xl tabular-nums">{fila.data.total_reservas || 0}</div>
          </div>
          <div data-testid="kpi-conferencia-saldo" className="bg-[#F5E4DD] text-[#7c3a20] border border-[#D4A652]/25 rounded-lg px-4 py-3">
            <div className="text-[11px] uppercase tracking-widest opacity-70">Saldo em aberto</div>
            <div className="font-serif text-xl tabular-nums">{brl(fila.data.total_saldo || 0)}</div>
          </div>
          <div data-testid="kpi-conferencia-filtro" className="bg-stone-50 border border-stone-200 rounded-lg px-4 py-3">
            <div className="text-[11px] uppercase tracking-widest text-stone-500">Após busca</div>
            <div className="font-serif text-xl tabular-nums">{itensFiltrados.length}</div>
          </div>
        </div>
      )}

      {/* Corpo */}
      {fila.loading ? (
        <div data-testid="conferencia-loading" className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-10 text-center text-stone-500">
          Carregando fila...
        </div>
      ) : fila.error && fila.error !== "auth" ? (
        <div data-testid="conferencia-erro" className="bg-[#FFFFFF] border border-[#D4A652]/30 rounded-lg p-10 text-center text-[#D4A652]">
          Não foi possível carregar a fila.
        </div>
      ) : itensFiltrados.length === 0 ? (
        <div data-testid="conferencia-empty" className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-12 text-center">
          <div className="w-14 h-14 rounded-full bg-[#EAF0EA] flex items-center justify-center mx-auto mb-4 text-[#083A6B]">
            <ClipboardCheck size={22} strokeWidth={1.5} />
          </div>
          <div className="font-serif text-2xl text-stone-900 mb-2">Nenhuma reserva na fila.</div>
          <p className="text-sm text-stone-600 max-w-md mx-auto">
            Ajuste os filtros ou tire "Com saldo em aberto" para incluir reservas quitadas.
          </p>
        </div>
      ) : (
        <>
          {/* Mobile */}
          <div className="md:hidden space-y-3">
            {itensFiltrados.map((i) => (
              <div key={i.reserva_id} data-testid={`conferencia-card-${i.reserva_id}`}
                className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <div className="font-serif text-lg text-stone-900 truncate">{i.hospede_nome || "—"}</div>
                    <div className="text-xs text-stone-500">{i.pousada_nome} · {fmtDate(i.data_entrada)} → {fmtDate(i.data_saida)}</div>
                  </div>
                  <StatusBadge status={i.status_pagamento} />
                </div>
                <div className="text-xs text-stone-600 space-y-0.5 mb-3">
                  <div><b>Contratado:</b> <span className="tabular-nums">{brl(i.valor_contratado)}</span></div>
                  <div><b>Recebido:</b> <span className="tabular-nums text-[#083A6B]">{brl(i.valor_recebido)}</span></div>
                  <div><b>Saldo:</b> <span className="tabular-nums text-[#D4A652]">{brl(i.saldo)}</span></div>
                  {i.sinal_status && i.sinal_status !== "sem_sinal" && (
                    <div className="text-[10px] text-stone-500">Sinal: {i.sinal_status} · {brl(i.sinal_valor)}</div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => abrirDetalhe(i)}
                    data-testid={`btn-conferencia-detalhe-${i.reserva_id}`}><Eye size={14} /> Detalhes</Button>
                  {podeConferir && i.saldo > 0 && (
                    <Button size="sm" variant="outline" onClick={() => abrirConferir(i)}
                      data-testid={`btn-conferencia-conferir-${i.reserva_id}`}>
                      <CheckCircle2 size={14} /> Conferir
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop */}
          <div className="hidden md:block bg-[#FFFFFF] border border-stone-200 rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50 hover:bg-stone-50">
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Entrada</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Hóspede</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Pousada</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Contratado</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Recebido</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Saldo</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500">Status</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-stone-500 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {itensFiltrados.map((i) => (
                  <TableRow key={i.reserva_id} data-testid={`conferencia-row-${i.reserva_id}`}>
                    <TableCell className="text-stone-700 text-sm whitespace-nowrap">{fmtDate(i.data_entrada)}</TableCell>
                    <TableCell className="text-stone-900 text-sm">
                      <div className="truncate max-w-[220px]">{i.hospede_nome || "—"}</div>
                      {i.hospede_documento && (
                        <div className="text-[10px] text-stone-500 font-mono">{i.hospede_documento}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-stone-700 text-sm">{i.pousada_nome || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{brl(i.valor_contratado)}</TableCell>
                    <TableCell className="text-right tabular-nums text-[#083A6B]">{brl(i.valor_recebido)}</TableCell>
                    <TableCell className={`text-right tabular-nums ${i.saldo > 0 ? "text-[#D4A652]" : "text-stone-500"}`}>{brl(i.saldo)}</TableCell>
                    <TableCell><StatusBadge status={i.status_pagamento} /></TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => abrirDetalhe(i)}
                          aria-label="Ver detalhes" data-testid={`btn-conferencia-detalhe-${i.reserva_id}`}>
                          <Eye size={14} />
                        </Button>
                        {podeConferir && i.saldo > 0 && (
                          <Button size="sm" variant="ghost" onClick={() => abrirConferir(i)}
                            aria-label="Conferir pagamento" data-testid={`btn-conferencia-conferir-${i.reserva_id}`}>
                            <CheckCircle2 size={14} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {/* Detalhes */}
      <Dialog open={detalheOpen} onOpenChange={(v) => { setDetalheOpen(v); if (!v) { setDetalheAlvo(null); setPagamentos([]); setAuditoria([]); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dialog-conferencia-detalhe">
          <DialogHeader>
            <DialogTitle>Detalhes da reserva</DialogTitle>
            <DialogDescription>
              Dados oficiais e auditoria. Pagamentos estornados permanecem visíveis (soft-cancel).
            </DialogDescription>
          </DialogHeader>
          {detalheAlvo && (
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm mb-4">
              <div><div className="text-[11px] uppercase text-stone-500">Hóspede</div>{detalheAlvo.hospede_nome || "—"}</div>
              <div><div className="text-[11px] uppercase text-stone-500">Pousada</div>{detalheAlvo.pousada_nome || "—"}</div>
              <div><div className="text-[11px] uppercase text-stone-500">Entrada → Saída</div>{fmtDate(detalheAlvo.data_entrada)} → {fmtDate(detalheAlvo.data_saida)}</div>
              <div><div className="text-[11px] uppercase text-stone-500">Status</div><StatusBadge status={detalheAlvo.status_pagamento} /></div>
              <div><div className="text-[11px] uppercase text-stone-500">Contratado</div><b className="tabular-nums">{brl(detalheAlvo.valor_contratado)}</b></div>
              <div><div className="text-[11px] uppercase text-stone-500">Recebido</div><b className="tabular-nums text-[#083A6B]">{brl(detalheAlvo.valor_recebido)}</b></div>
              <div><div className="text-[11px] uppercase text-stone-500">Saldo</div><b className="tabular-nums text-[#D4A652]">{brl(detalheAlvo.saldo)}</b></div>
              <div><div className="text-[11px] uppercase text-stone-500">Sinal</div>{detalheAlvo.sinal_status} {detalheAlvo.sinal_valor ? `· ${brl(detalheAlvo.sinal_valor)}` : ""}</div>
              <div className="col-span-2 text-[11px] text-stone-500 font-mono truncate">ID reserva: {detalheAlvo.reserva_id}</div>
            </div>
          )}

          <div className="border-t border-stone-200 pt-3 mb-2">
            <div className="text-[11px] uppercase text-stone-500 mb-2">Pagamentos ({pagamentos.length})</div>
            {detalheLoading ? (
              <div className="text-center text-stone-500 py-3 text-sm">Carregando...</div>
            ) : pagamentos.length === 0 ? (
              <div className="text-stone-500 text-sm">Nenhum pagamento registrado.</div>
            ) : (
              <div className="space-y-2">
                {pagamentos.map((p) => (
                  <div key={p.id} data-testid={`conferencia-pag-${p.id}`}
                    className={`border rounded p-3 text-xs ${p.estornado ? "border-stone-300 bg-stone-50 opacity-60" : "border-stone-200 bg-white"}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <b className="tabular-nums text-sm">{brl(p.valor)}</b>
                          <span className="text-stone-500">{fmtDate(p.data)}</span>
                          <span className="text-stone-500">· {p.forma}</span>
                          <span className="text-[10px] uppercase tracking-widest text-stone-500">{p.origem}</span>
                          {p.estornado && <span className="text-[10px] uppercase tracking-widest text-[#D4A652]">estornado</span>}
                        </div>
                        {p.observacao && <div className="text-stone-600 mt-1 truncate">{p.observacao}</div>}
                        {p.estorno_motivo && (
                          <div className="text-[10px] text-[#7c3a20] mt-1">Motivo estorno: {p.estorno_motivo}</div>
                        )}
                        <div className="text-[10px] text-stone-400 font-mono mt-1">{p.id}</div>
                      </div>
                      {podeEstornar && !p.estornado && (
                        <Button size="sm" variant="outline" onClick={() => abrirEstorno(p)}
                          data-testid={`btn-conferencia-estornar-${p.id}`}>
                          <RotateCcw size={12} /> Estornar
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-stone-200 pt-3">
            <div className="text-[11px] uppercase text-stone-500 mb-2">Auditoria (append-only, {auditoria.length})</div>
            {auditoria.length === 0 ? (
              <div className="text-stone-500 text-sm">Sem eventos de auditoria.</div>
            ) : (
              <ul className="space-y-1 text-xs text-stone-600 max-h-40 overflow-y-auto">
                {auditoria.map((a) => (
                  <li key={a.id || `${a.timestamp_utc}-${a.acao}`}>
                    <ArrowRight size={11} className="inline text-stone-400" /> <b>{a.acao}</b>
                    {a.autor_email ? ` por ${a.autor_email}` : ""} em {a.timestamp_utc?.slice(0, 16).replace("T", " ") || "—"}
                    {a.motivo && ` · motivo: ${a.motivo}`}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {detalheAlvo && podeConferir && detalheAlvo.saldo > 0 && (
            <DialogFooter className="mt-3">
              <Button data-testid="btn-conferencia-conferir-dialog" onClick={() => abrirConferir(detalheAlvo)}>
                <CheckCircle2 size={14} /> Conferir novo pagamento
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Conferir / Cobrança */}
      <Dialog open={!!conferirAlvo} onOpenChange={(v) => { if (!v) setConferirAlvo(null); }}>
        <DialogContent className="max-w-md" data-testid="dialog-conferencia-conferir">
          <DialogHeader>
            <DialogTitle>Conferir pagamento</DialogTitle>
            <DialogDescription>
              Registra um pagamento manual na reserva via <b>POST /conferencia/pagamentos</b>. Origem: <b>manual</b>. Não altera PagBank nem /sinal/pagar.
            </DialogDescription>
          </DialogHeader>
          {conferirAlvo && (
            <div className="text-sm text-stone-700 space-y-1">
              <div className="text-xs text-stone-500">{conferirAlvo.hospede_nome} · {conferirAlvo.pousada_nome}</div>
              <div>Contratado: <b className="tabular-nums">{brl(conferirAlvo.valor_contratado)}</b> · Saldo: <b className="tabular-nums text-[#D4A652]">{brl(conferirAlvo.saldo)}</b></div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cf-valor">Valor *</Label>
              <Input id="cf-valor" data-testid="input-conferencia-valor" type="number" step="0.01" min="0.01"
                value={conferirForm.valor}
                onChange={(e) => setConferirForm({ ...conferirForm, valor: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="cf-data">Data *</Label>
              <Input id="cf-data" data-testid="input-conferencia-data" type="date"
                value={conferirForm.data}
                onChange={(e) => setConferirForm({ ...conferirForm, data: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label htmlFor="cf-forma">Forma *</Label>
              <Select value={conferirForm.forma}
                onValueChange={(v) => setConferirForm({ ...conferirForm, forma: v })}>
                <SelectTrigger id="cf-forma" data-testid="select-conferencia-forma"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FORMAS.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label htmlFor="cf-conta">Conta Financeira *</Label>
              <Select value={conferirForm.conta_financeira_id}
                onValueChange={(v) => setConferirForm({ ...conferirForm, conta_financeira_id: v })}>
                <SelectTrigger id="cf-conta" data-testid="select-conferencia-conta-financeira">
                  <SelectValue placeholder="Selecione a conta de recebimento" />
                </SelectTrigger>
                <SelectContent>
                  {contasFinanceiras.length === 0 ? (
                    <SelectItem value="__none__" disabled>Nenhuma conta ativa cadastrada</SelectItem>
                  ) : (
                    contasFinanceiras.map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`opt-conferencia-conta-fin-${c.id}`}>
                        {c.nome}{c.banco ? ` — ${c.banco}` : ""}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label htmlFor="cf-ref">Referência externa</Label>
              <Input id="cf-ref" data-testid="input-conferencia-ref"
                value={conferirForm.referencia_externa}
                onChange={(e) => setConferirForm({ ...conferirForm, referencia_externa: e.target.value })}
                placeholder="TXID PIX, autorização cartão, nº boleto..." />
            </div>
            <div className="col-span-2">
              <Label htmlFor="cf-obs">Observação</Label>
              <Input id="cf-obs" data-testid="input-conferencia-obs"
                value={conferirForm.observacao}
                onChange={(e) => setConferirForm({ ...conferirForm, observacao: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConferirAlvo(null)} disabled={conferindo}>Cancelar</Button>
            <Button data-testid="btn-confirmar-conferir" onClick={executarConferir} disabled={conferindo || !conferirForm.conta_financeira_id}>
              {conferindo ? "Registrando..." : "Confirmar pagamento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Estorno */}
      <Dialog open={!!estornoAlvo} onOpenChange={(v) => { if (!v) { setEstornoAlvo(null); setEstornoMotivo(""); } }}>
        <DialogContent className="max-w-md" data-testid="dialog-conferencia-estornar">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-[#D4A652]" /> Estornar pagamento?
            </DialogTitle>
            <DialogDescription>
              Soft-cancel oficial via <b>POST /pagamentos/{"{id}"}/estornar</b>. Preserva o registro. Motivo obrigatório. PagBank e /sinal/pagar não são alterados.
            </DialogDescription>
          </DialogHeader>
          {estornoAlvo && (
            <div className="text-sm text-stone-700 space-y-1">
              <div>Valor: <b className="tabular-nums">{brl(estornoAlvo.valor)}</b> · {fmtDate(estornoAlvo.data)} · {estornoAlvo.forma}</div>
              <div className="text-[10px] text-stone-500 font-mono">{estornoAlvo.id}</div>
            </div>
          )}
          <div>
            <Label htmlFor="est-motivo">Motivo *</Label>
            <Input id="est-motivo" data-testid="input-conferencia-estorno-motivo"
              value={estornoMotivo} onChange={(e) => setEstornoMotivo(e.target.value)}
              placeholder="Descreva o motivo do estorno" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEstornoAlvo(null); setEstornoMotivo(""); }} disabled={estornando}>
              Voltar
            </Button>
            <Button data-testid="btn-confirmar-estornar" onClick={executarEstorno}
              disabled={estornando || !estornoMotivo.trim()}>
              {estornando ? "Estornando..." : "Confirmar estorno"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
